/**
 * Custom hook to sync draft data with Redux store
 * Consolidates all Redux dispatch operations for draft loading/saving
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  setBusinessDoc,
  setCertificateOfIncorporationDoc,
  setProofOfBankAccountDetailsDoc,
  setSupportingDocumentsWithSuppliersDoc,
} from "../../../features/merchant/doc";
import { hydrateDisBizState } from "../../../features/distributor/biz";
import { hydrateDisBankState } from "../../../features/distributor/bank";

export interface DraftData {
  businessProfile?: any[];
  certificateOfIncorporation?: any[];
  proofOfBankAccountDetails?: any[];
  supportingDocumentsWithSuppliers?: any[];
  companyName?: string;
  companyRegistrationNo?: string;
  legalStructure?: string;
  legalStructureOthers?: string;
  legalEntityAddress?: string;
  industryGroup?: string;
  industry?: string;
  countryISOCode?: string;
  country?: string;
  region?: string;
  city?: string;
  postalCode?: string;
  website?: string;
  userFirstName?: string;
  userLastName?: string;
  bankName?: string;
  bankCode?: string;
  accountType?: string;
  accountNumber?: string;
  accountHolderName?: string;
  swiftCode?: string;
  bankAddress?: string;
  bankCountry?: string;
  currency?: string;
}

/**
 * Hook to sync draft data with Redux store
 */
export const useDraftSync = () => {
  const dispatch = useDispatch();

  /**
   * Sync all draft data to Redux store
   * Uses hydrate pattern for efficient bulk updates
   */
  const syncDraftToRedux = useCallback(
    (data: DraftData) => {
      // Sync document uploads (4 documents) - Use unwrapped payloads
      dispatch(setBusinessDoc(data.businessProfile || []));
      dispatch(
        setCertificateOfIncorporationDoc(data.certificateOfIncorporation || [])
      );
      dispatch(
        setProofOfBankAccountDetailsDoc(data.proofOfBankAccountDetails || [])
      );
      dispatch(
        setSupportingDocumentsWithSuppliersDoc(
          data.supportingDocumentsWithSuppliers || []
        )
      );

      // Sync business info - Single dispatch with hydrateDisBizState
      dispatch(
        hydrateDisBizState({
          company: data.companyName ?? null,
          regNumber: data.companyRegistrationNo ?? null,
          legalStructure: data.legalStructure ?? null,
          legalStructureOthers: data.legalStructureOthers ?? null,
          countryISOCode: data.countryISOCode ?? data.country ?? null,
          region: data.region ?? null,
          city: data.city ?? null,
          address: data.legalEntityAddress ?? null,
          postal: data.postalCode?.toString() ?? null,
          website: data.website ?? null,
          industryGroup: data.industryGroup ?? null,
          typeOfInstitute: data.industry ?? null,
          userFirstName: data.userFirstName ?? null,
          userLastName: data.userLastName ?? null,
        })
      );

      // Sync bank info - Single dispatch with hydrateDisBankState
      dispatch(
        hydrateDisBankState({
          bankName: data.bankName ?? null,
          bankCode: data.bankCode ?? null,
          accountType: data.accountType ?? null,
          accountNumber: data.accountNumber ?? null,
          accountHolderName: data.accountHolderName ?? null,
          swiftCode: data.swiftCode ?? null,
          bankAddress: data.bankAddress ?? null,
          bankCountry: data.bankCountry ?? null,
          currency: data.currency ?? null,
        })
      );
    },
    [dispatch]
  );

  return { syncDraftToRedux };
};
